import { JSONSchema7, JSONSchema7TypeName } from 'json-schema';
import {
  AttributeFormat,
  AttributeJsonSchema,
  AttributeProperty,
  AttributePropertyType,
  AttributeStyle,
  GenericAttribute
} from 'src/types/attributes';

export const propertyType = <T extends AttributeFormat>(
  attributeStyle: AttributeStyle | undefined,
  { path, type }: AttributeProperty<T>
): string => attributeStyle?.properties?.[path]?.overrides?.type || type;

export const propertyValue = <T extends AttributeFormat>(
  attributeValue: GenericAttribute<T> | undefined,
  path: string
): AttributeProperty<T>['value'] =>
  attributeValue?.properties.find((property) => property.path === path)?.value;

export const updatePropertyValue = <T extends AttributeFormat>(
  { properties, ...attribute }: GenericAttribute<T>,
  path: string,
  value: AttributeProperty<T>['value']
): GenericAttribute<T> => ({
  ...attribute,
  // replace or create the value for the given path
  // (ensure it is created if it does not exist)
  properties: [
    ...properties.filter((property) => property.path !== path),
    { type: 'atomic', path, value } as AttributeProperty<T>
  ]
});

const atomic = (name?: JSONSchema7TypeName): boolean =>
  !!name && ['string', 'integer', 'null'].includes(name);

const array = (name?: JSONSchema7TypeName): boolean => name === 'array';

const is = (
  { type }: JSONSchema7,
  callback: (name?: JSONSchema7TypeName) => boolean
) => (typeof type === 'object' ? type.every(callback) : callback(type));

const parseRef = (ref: string) =>
  // TODO: validate property type
  ref.substring(2).split('/', 2) as [AttributePropertyType, string];

/**
 * Property definition extracted from schema (without value)
 */
interface PropertyDefinition {
  type: AttributePropertyType;
  path: string;
  index: number;
}

/**
 * Extract property definitions from JSON schema
 */
export const extractPropertyDefinitions = (
  schema: AttributeJsonSchema
): PropertyDefinition[] =>
  Object.entries(schema.properties || []).flatMap(([key, property], index) => {
    // extract from x-refs
    if (property['x-refs'])
      return property['x-refs'].map(parseRef).map(([type, path]) => ({
        type,
        path: path,
        index
      }));
    // extract enum
    if (property.enum)
      return [
        {
          type: 'enum',
          path: `$.${key}`,
          index
        }
      ];
    // extract atomic value
    if (is(property, atomic))
      return [
        {
          type: 'atomic',
          path: `$.${key}`,
          index
        }
      ];
    // extract array value
    if (is(property, array))
      return [
        {
          type: 'array',
          path: `$.${key}`,
          index
        }
      ];
    // type not supported
    return [
      {
        type: 'const',
        path: `$.${key}`,
        index
      }
    ];
  });
